import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { userSelector } from 'redux/selectors';

/**
 * Hook to check if google_sync feature is enabled.
 *
 * @param {Object} options - Optional configuration
 * @param {Object} options.network - Specific network to check (takes precedence if provided)
 * @returns {boolean} - Whether google_sync feature is enabled
 *
 * Behavior:
 * - If a specific network is provided, checks only that network's features
 * - Otherwise, checks if any of the user's managing OR allying networks have the feature
 *
 * Usage examples:
 * - Settings pages (no active network): useHasGoogleSyncFeature()
 * - Ally onboarding (specific network): useHasGoogleSyncFeature({ network: allyNetwork })
 * - Network admin pages: useHasGoogleSyncFeature({ network: activeNetwork })
 */
export const useHasGoogleSyncFeature = ({ network } = {}) => {
  const user = useSelector(userSelector);

  return useMemo(() => {
    // If a specific network is provided, check only that network
    if (network) {
      return network.features?.includes('google_sync') ?? false;
    }

    // Otherwise, check both managing and allying collections
    const hasInManaging = user.managingCollections?.some((n) => n.features?.includes('google_sync')) ?? false;
    const hasInAllying = user.allyingCollections?.some((n) => n.features?.includes('google_sync')) ?? false;

    return hasInManaging || hasInAllying;
  }, [network, user.managingCollections, user.allyingCollections]);
};
